import React from "react";
import { useSelector } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { formatDisplayDate } from "../../../../common/utils/date";
import { RootStateServiceLogicBuilder } from "../../store/service-logic-builder-slice";
import {
  capitalizeFirstLetter,
  getChoiceLabelByIndex,
  getDisplayCategoryName,
  getFieldDisplayLabel,
  getFieldOptionById,
  getOperatorDisplayText,
} from "../../utils/ruleFormatters";
import { useAuth } from "../../../../providers/AuthProvider";
import {
  isMeasurementUnitParameter,
  formatHeightDisplay,
  convertWeightToDisplay,
} from "../logicBuilder/measurementUnitHelpers";

interface PrescriptionRationaleProps {
  // The logic rules carried on a prescription service or lab panel.
  rules: any;
  // Drop the section's own top margin when the parent card already separates
  // children with a gap (e.g. the lab-panel card) — avoids a double gap.
  disableTopMargin?: boolean;
}

// Renders the "Rationale" section (the logic conditions that triggered a
// prescription item). Shared by the service and lab-panel cards so both show
// identical rationale details.
const PrescriptionRationale: React.FC<PrescriptionRationaleProps> = ({
  rules,
  disableTopMargin = false,
}) => {
  const theme: Theme = useTheme();
  const { AuthUser } = useAuth();
  const categoryOptions = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.categoryOptions
  );

  // Get unitType from authenticated user (1 = metric, 2 = imperial)
  const unitType = AuthUser.unitType;

  if (!rules?.list?.[0]?.conditions?.length) {
    return null;
  }

  return (
    <div
      style={{
        ...styles.rationaleInformation(theme),
        ...(disableTopMargin ? { marginTop: 0 } : {}),
      }}
    >
      <div style={styles.serviceInformationTitle(theme)}>{`Rationale ${
        rules.list[0].conditions.filter((c: any) => c.valid).length > 1 &&
        rules.list[0].match
          ? capitalizeFirstLetter(rules.list[0].match)
          : ""
      }`}</div>
      <div>
        {rules.list[0].conditions.filter((c: any) => c.valid).length > 0 ? (
          <div>
            {rules.list[0].conditions
              .filter((c: any) => c.valid)
              .map((condition: any, condIndex: number) => (
                <div key={condIndex}>
                  <div style={styles.conditionLabel(theme)}>
                    {condition.object_name
                      ? `${condIndex + 1}. ${getDisplayCategoryName(
                          condition.object_name
                        )} ${
                          condition.fields.filter((f: any) => f.valid).length >
                            1 && condition.match
                            ? `(${capitalizeFirstLetter(condition.match)})`
                            : ""
                        }`
                      : ""}
                  </div>
                  <ul style={styles.paramsList}>
                    {condition.fields
                      .filter((f: any) => f.valid)
                      .map((f: any, fieldIndex: number) => {
                        const fieldOption = getFieldOptionById(
                          categoryOptions,
                          condition.object_name,
                          f.name
                        );
                        const fieldLabel = getFieldDisplayLabel(
                          categoryOptions,
                          condition.object_name,
                          f.name
                        );
                        let fieldUnit = fieldOption?.unit || "";

                        // Get the proper value display - either choice label or raw value with unit
                        let displayValue: string;

                        if (
                          fieldOption?.choices &&
                          fieldOption.choices.length > 0 &&
                          f.value !== null
                        ) {
                          // For biometric/lifestyle questions with choices, show the choice label
                          displayValue = getChoiceLabelByIndex(
                            fieldOption,
                            f.value
                          );
                        } else if (
                          fieldOption?.type === "datepicker" &&
                          f.value !== null
                        ) {
                          // Date criteria (e.g. birth_date) — format via shared util
                          displayValue = formatDisplayDate(f.value);
                        } else {
                          // Handle measurement unit conversion
                          let valueToDisplay = f.value;

                          if (
                            isMeasurementUnitParameter(f.name) &&
                            f.value !== null
                          ) {
                            if (f.name === "height") {
                              // Format height display based on unitType
                              displayValue = formatHeightDisplay(
                                Number(f.value),
                                unitType
                              );
                              return (
                                <li
                                  key={fieldIndex}
                                  style={styles.conditionValue(theme)}
                                >
                                  <span>{fieldLabel}</span>
                                  {f.operator && (
                                    <span
                                      style={{
                                        color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                                      }}
                                    >{` ${getOperatorDisplayText(
                                      f.operator
                                    ).toLowerCase()} `}</span>
                                  )}
                                  <span>{displayValue}</span>
                                </li>
                              );
                            } else if (
                              f.name === "current_weight" ||
                              f.name === "target_weight"
                            ) {
                              if (unitType === 2) {
                                // Convert kg to lbs (rounded)
                                valueToDisplay = convertWeightToDisplay(
                                  Number(f.value),
                                  unitType
                                );
                                fieldUnit = "lbs";
                              } else {
                                // Metric - keep as kg with decimals
                                valueToDisplay = f.value;
                                fieldUnit = "kg";
                              }
                            }
                          }

                          // For numeric fields (like Pnoe Metrics), show value with unit
                          const shouldShowUnit =
                            fieldUnit && fieldUnit !== "high/low";
                          displayValue = shouldShowUnit
                            ? `${valueToDisplay}${
                                fieldUnit !== "%" ? " " : ""
                              }${fieldUnit}`
                            : valueToDisplay === 100
                            ? "High"
                            : valueToDisplay === 10
                            ? "Low"
                            : valueToDisplay?.toString() || "";
                        }

                        return (
                          <li
                            key={fieldIndex}
                            style={styles.conditionValue(theme)}
                          >
                            <span>{fieldLabel}</span>
                            {f.operator && (
                              <span
                                style={{
                                  color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                                }}
                              >{` ${getOperatorDisplayText(
                                f.operator
                              ).toLowerCase()} `}</span>
                            )}
                            <span>{displayValue}</span>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              ))}
          </div>
        ) : (
          <div>No conditions defined.</div>
        )}
      </div>
    </div>
  );
};

export default PrescriptionRationale;

const styles = {
  rationaleInformation: (theme: Theme) => ({
    marginTop: "24px",
    borderTop: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    paddingTop: "24px",
  }),
  serviceInformationTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    lineHeight: "22px",
    marginBottom: "16px",
  }),
  conditionLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    paddingBottom: "8px",
  }),
  paramsList: {
    listStylePosition: "inside" as "inside",
  },
  conditionValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
};
